import axios from 'axios';
import Cookies from 'js-cookie';

// Dynamic API URL detection
const getAPIBaseURL = () => {
  // If environment variable is set, use it
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // Auto-detect based on current window location
  let protocol = window.location.protocol; // http: or https:
  const hostname = window.location.hostname;
  
  // Force HTTPS for production domain
  if (hostname === 'calendar.linked-u.com') {
    protocol = 'https:';
  }
  
  // When using Nginx, API is proxied through /api path (no port needed)
  return `${protocol}//${hostname}/api`;
};

const API_BASE_URL = getAPIBaseURL();

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 second timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests and log them
api.interceptors.request.use((config) => {
  const token = Cookies.get('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors and provide better error information
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only redirect to login on actual authentication failures
    // Don't redirect on network errors or other transient issues
    if (error.response?.status === 401 && error.response?.data?.detail) {
      // Only logout if we get a clear authentication error from the server
      Cookies.remove('access_token');
      // Use replace to avoid back button issues
      if (!window.location.pathname.includes('/login')) {
        window.location.replace('/login');
      }
    }
    return Promise.reject(error);
  }
);

// Authentication API calls
export const authAPI = {
  login: (username, password) => api.post('/auth/login', { username, password }),
  register: (userData) => api.post('/auth/register', userData),
  getMe: () => api.get('/auth/me'),
  getUsers: () => api.get('/auth/users'),
  // Lark OAuth
  getLarkAuthUrl: () => api.get('/auth/lark/url'),
  larkCallback: (code, state) => api.post('/auth/lark/callback', { code, state }),
};

// User API calls (consolidated users with employee information)
export const userAPI = {
  getAll: (params = {}) => api.get('/users/', { params }),
  getById: (id) => api.get(`/users/${id}`),
  getMe: () => api.get('/users/me'),
  create: (user) => api.post('/users/', user),
  update: (id, user) => api.put(`/users/${id}`, user),
  delete: (id) => api.delete(`/users/${id}`),
  getByBranch: (branchId, includeMoveable = true) => 
    api.get(`/users/by-branch/${branchId}/`, { params: { include_moveable: includeMoveable } }),
  getMoveableStaff: () => api.get('/users/moveable-staff/'),
  getPermissions: () => api.get('/user/permissions/'),
};

// Branch API calls
export const branchAPI = {
  getAll: () => api.get('/branches/'),
  getById: (id) => api.get(`/branches/${id}/`),
  create: (branch) => api.post('/branches/', branch),
  update: (id, branch) => api.put(`/branches/${id}/`, branch),
  delete: (id) => api.delete(`/branches/${id}/`),
  getStaff: (id) => api.get(`/branches/${id}/staff/`),
};

// Holiday Group API calls
export const holidayGroupAPI = {
  getAll: () => api.get('/holiday-groups/'),
  getById: (id) => api.get(`/holiday-groups/${id}/`),
  create: (holidayGroup) => api.post('/holiday-groups/', holidayGroup),
  update: (id, holidayGroup) => api.put(`/holiday-groups/${id}/`, holidayGroup),
  delete: (id) => api.delete(`/holiday-groups/${id}/`),
  getHolidays: (id, year = null) => api.get(`/holiday-groups/${id}/holidays/`, { params: { year } }),
  getUsers: (id) => api.get(`/holiday-groups/${id}/users/`),
};

// Holiday API calls
export const holidayAPI = {
  getAll: (params = {}) => api.get('/holidays/', { params }),
  getById: (id) => api.get(`/holidays/${id}/`),
  create: (holiday) => api.post('/holidays/', holiday),
  update: (id, holiday) => api.put(`/holidays/${id}/`, holiday),
  delete: (id) => api.delete(`/holidays/${id}/`),
  getCurrentYear: (holidayGroupId = null) => 
    api.get('/holidays/current-year/', { params: { holiday_group_id: holidayGroupId } }),
  getUpcoming: (holidayGroupId = null, days = 30) => 
    api.get('/holidays/upcoming/', { params: { holiday_group_id: holidayGroupId, days } }),
};

// Shift API calls
export const shiftAPI = {
  getAll: (params = {}) => api.get('/shifts/', { params }),
  getById: (id) => api.get(`/shifts/${id}/`),
  create: (shift) => api.post('/shifts/', shift),
  update: (id, shift) => api.put(`/shifts/${id}/`, shift),
  delete: (id) => api.delete(`/shifts/${id}/`),
  getByRole: (role) => api.get(`/shifts/by-role/${role}/`),
  getScheduleInfo: () => api.get('/shifts/schedule-info/'),
};

// User Branch Assignment API calls
export const userBranchAssignmentAPI = {
  getAll: (params = {}) => api.get('/user-branch-assignments/', { params }),
  getById: (id) => api.get(`/user-branch-assignments/${id}`),
  create: (assignment) => api.post('/user-branch-assignments/', assignment),
  update: (id, assignment) => api.put(`/user-branch-assignments/${id}`, assignment),
  delete: (id) => api.delete(`/user-branch-assignments/${id}`),
  getByUser: (userId, startDate = null, endDate = null) => 
    api.get(`/user-branch-assignments/user/${userId}`, { params: { start_date: startDate, end_date: endDate } }),
  getByBranch: (branchId, assignedDate = null, shiftType = null) => 
    api.get(`/user-branch-assignments/branch/${branchId}`, { params: { assigned_date: assignedDate, shift_type: shiftType } }),
};

// Work Assignment API calls (updated for multi-branch)
export const workAssignmentAPI = {
  getAll: (params = {}) => api.get('/work-assignments/', { params }),
  getById: (id) => api.get(`/work-assignments/${id}/`),
  create: (assignment) => api.post('/work-assignments/', assignment),
  createBulk: (assignments) => api.post('/work-assignments/bulk', assignments),
  update: (id, assignment) => api.put(`/work-assignments/${id}/`, assignment),
  delete: (id) => api.delete(`/work-assignments/${id}/`),
  getUserCalendar: (userId, startDate = null, endDate = null) => 
    api.get(`/work-assignments/calendar/${userId}/`, { params: { start_date: startDate, end_date: endDate } }),
  getMyCalendar: (startDate = null, endDate = null) => 
    api.get('/work-assignments/my/calendar/', { params: { start_date: startDate, end_date: endDate } }),
  getBranchCalendar: (branchId, startDate = null, endDate = null) => 
    api.get(`/work-assignments/branch/${branchId}/calendar/`, { params: { start_date: startDate, end_date: endDate } }),
  getBranchDailyAssignments: (branchId, assignmentDate) => 
    api.get(`/work-assignments/branch/${branchId}/date/${assignmentDate}/`),
  createAssignment: (assignment) => api.post('/work-assignments/', assignment), // Alias for create
};

// System API calls
export const systemAPI = {
  getInfo: () => api.get('/system/info/'),
  getPermissions: () => api.get('/user/permissions/'),
};

// Leave Management API calls
export const leaveAPI = {
  // Leave Types
  getLeaveTypes: () => api.get('/leave/types/'),
  createLeaveType: (leaveType) => api.post('/leave/types/', leaveType),
  
  // Leave Balances
  getLeaveBalances: (params = {}) => api.get('/leave/balances/', { params }),
  getUserLeaveBalances: (userId, year) => api.get(`/leave/balances/${userId}/${year}/`),
  bulkAssignLeave: (assignment) => api.post('/leave/balances/bulk/', assignment),
  updateLeaveBalance: (balanceId, update) => api.put(`/leave/balances/${balanceId}/`, update),
  
  // Leave Requests
  getLeaveRequests: (params = {}) => api.get('/leave/requests/', { params }),
  createLeaveRequest: (request) => api.post('/leave/requests/', request),
  approveLeaveRequest: (requestId) => api.put(`/leave/requests/${requestId}/approve/`),
  rejectLeaveRequest: (requestId) => api.put(`/leave/requests/${requestId}/reject/`),
  deleteLeaveRequest: (requestId) => api.delete(`/leave/requests/${requestId}/`),
  
  // Calendar Integration
  getLeaveCalendar: (year, month, userId = null) => 
    api.get(`/leave/calendar/${year}/${month}/`, { params: { user_id: userId } }),
};

// Audit Logging API
export const auditAPI = {
  // Get audit logs with filtering
  getAuditLogs: (params = {}) => api.get('/audit/logs', { params }),
  
  // Get audit summary
  getAuditSummary: (days = 7) => api.get('/audit/logs/summary', { params: { days } }),
  
  // Get available actions for filtering
  getAvailableActions: () => api.get('/audit/logs/actions'),
  
  // Get available resources for filtering  
  getAvailableResources: () => api.get('/audit/logs/resources'),
  
  // Get audit trail for specific user
  getUserAuditTrail: (userId, params = {}) => api.get(`/audit/logs/user/${userId}`, { params })
};

// Backward compatibility - alias userAPI as employeeAPI
export const employeeAPI = userAPI;

export default api;